import { ReactNode } from 'react';
import { FieldDescription, FieldLegend, FieldSet } from '@/Domain/components/Shared/Field/Field';
import { cn } from '@/Domain/utils/cn';

interface IntegrationSectionProps {
  title: string;
  description?: ReactNode;
  /** Optional control rendered right-aligned in the title row (e.g. a refresh icon). */
  action?: ReactNode;
  /** Optional node rendered inline right after the title (e.g. an upgrade badge). */
  titleAddon?: ReactNode;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
}

/**
 * The standard frame for every body section of an integration sheet (What it
 * does, Prerequisites, Connect, Enable, Settings): a titled `FieldSet` with
 * optional description, so every section shares the same heading and spacing.
 */
export const IntegrationSection = ({
  title,
  description,
  action,
  titleAddon,
  children,
  className,
  ...props
}: IntegrationSectionProps) => (
  <FieldSet className={cn('space-y-4', className)} data-testid={props['data-testid']}>
    {action || titleAddon ? (
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <FieldLegend variant="legend" className="text-base">
            {title}
          </FieldLegend>
          {titleAddon}
        </div>
        {action}
      </div>
    ) : (
      <FieldLegend variant="legend" className="text-base">
        {title}
      </FieldLegend>
    )}
    {description ? <FieldDescription className="pb-0">{description}</FieldDescription> : null}
    {children}
  </FieldSet>
);

export default IntegrationSection;
